import api from './api';

export interface ZertifikatInfo {
  zertifikatID: number;
  name: string;
  beschreibung?: string;
  subject?: string;
  aussteller?: string;
  fingerabdruck?: string;
  gueltigAb?: string;
  gueltigBis?: string;
  istStandard: boolean;
  status: string;
  istGueltig: boolean;
}

export interface ZertifikatUploadResult {
  success: boolean;
  zertifikatID?: number;
  subject?: string;
  aussteller?: string;
  gueltigBis?: string;
  errorMessage?: string;
}

export interface SignatureResult {
  success: boolean;
  signedFilePath?: string;
  signedAt?: string;
  signedBy?: string;
  errorMessage?: string;
}

export interface SignatureValidationResult {
  success: boolean;
  isSigned: boolean;
  isValid: boolean;
  message?: string;
  errorMessage?: string;
  signatures: SignatureInfo[];
}

export interface SignatureInfo {
  fieldName?: string;
  signedBy?: string;
  signedAt?: string;
  reason?: string;
  location?: string;
  isValid: boolean;
  validationResult?: string;
  certificateIssuer?: string;
  certificateExpiry?: string;
}

export const zertifikateService = {
  // Get all certificates for current user
  async getMeineZertifikate(): Promise<ZertifikatInfo[]> {
    const response = await api.get<ZertifikatInfo[]>('/zertifikate');
    return response.data;
  },

  // Upload a new certificate
  async uploadZertifikat(
    datei: File,
    passwort: string,
    name?: string,
    beschreibung?: string
  ): Promise<ZertifikatUploadResult> {
    const formData = new FormData();
    formData.append('datei', datei);
    formData.append('passwort', passwort);
    if (name) formData.append('name', name);
    if (beschreibung) formData.append('beschreibung', beschreibung);

    const response = await api.post<ZertifikatUploadResult>('/zertifikate/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete a certificate
  async deleteZertifikat(zertifikatId: number): Promise<void> {
    await api.delete(`/zertifikate/${zertifikatId}`);
  },

  // Set certificate as default
  async setDefault(zertifikatId: number): Promise<void> {
    await api.post(`/zertifikate/${zertifikatId}/set-default`);
  },

  // Sign a document by ID
  async signDocument(
    dokumentId: number,
    zertifikatPasswort: string,
    zertifikatId?: number,
    grund?: string,
    ort?: string,
    seite?: number
  ): Promise<SignatureResult> {
    const response = await api.post<SignatureResult>(`/zertifikate/sign/${dokumentId}`, {
      zertifikatId,
      zertifikatPasswort,
      grund,
      ort,
      seite,
    });
    return response.data;
  },

  // Sign an uploaded file and download
  async signFile(
    datei: File,
    zertifikatPasswort: string,
    zertifikatId?: number,
    grund?: string,
    ort?: string
  ): Promise<Blob> {
    const formData = new FormData();
    formData.append('datei', datei);
    formData.append('zertifikatPasswort', zertifikatPasswort);
    if (zertifikatId) formData.append('zertifikatId', zertifikatId.toString());
    if (grund) formData.append('grund', grund);
    if (ort) formData.append('ort', ort);

    const response = await api.post('/zertifikate/sign-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    });
    return response.data;
  },

  // Validate signatures in a file
  async validateFile(datei: File): Promise<SignatureValidationResult> {
    const formData = new FormData();
    formData.append('datei', datei);

    const response = await api.post<SignatureValidationResult>('/zertifikate/validate-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
